import React, {useState, useEffect} from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  Input,
} from "reactstrap";
import useProveedor from "../../../hooks/useProveedor";
import useProyectos from "../../../hooks/useProyectos";
import Select from "react-select";
import Swal from "sweetalert2";

const Tipo = [
  {value: "", label: "Selecciona el tipo de persona"},
  {value: "Fisica", label: "Fisica"},
  {value: "Moral", label: "Moral"},
];

const CODIGOS_PAIS = [
  {value: "+52", label: "+52 México"},
  {value: "+1", label: "+1 Estados Unidos/Canadá"},
  {value: "+34", label: "+34 España"},
  {value: "+57", label: "+57 Colombia"},
  {value: "+54", label: "+54 Argentina"},
  {value: "+56", label: "+56 Chile"},
  {value: "+51", label: "+51 Perú"},
];

/** "juan carlos" -> "Juan Carlos". Nombre/apellidos/razón social se ven igual de capturados aquí. */
const aTituloDeCaso = (texto) =>
  texto.replace(/\S+/g, (palabra) => palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase());

/** Separa "+52 2227796648" en código y los 10 dígitos; sin código asume México. */
const partirTelefono = (valor) => {
  const texto = String(valor ?? "").trim();
  if (texto.startsWith("+")) {
    const espacio = texto.indexOf(" ");
    const codigo = espacio === -1 ? texto : texto.slice(0, espacio);
    const numero = espacio === -1 ? "" : texto.slice(espacio + 1);
    return {codigo, numero: numero.replace(/\D/g, "").slice(0, 10)};
  }
  return {codigo: CODIGOS_PAIS[0].value, numero: texto.replace(/\D/g, "").slice(0, 10)};
};

export default function ModalAdd({
  modal,
  toggle,
  Id,
  Update,
  token,
  cliente_id,
  user_rol,
  TipoPersona,
  setTipoPersona,
  Nombre,
  setNombre,
  ApellidoP,
  setApellidoP,
  ApellidoM,
  setApellidoM,
  RFC,
  setRFC,
  Correo,
  setCorreo,
  Telefono,
  setTelefono,
  UsoCFDI,
  setUsoCFDI,
  Banco,
  setBanco,
  Clabe,
  setClabe,
  ProveedorGetService,
  veTodos,
  clienteActualId,
}) {
  const [SelectedTipo, setSelectedTipo] = useState([]);
  const [SelectedCFDI, setSelectedCFDI] = useState([]);
  const [SelectedCliente, setSelectedCliente] = useState(null);
  const [CodigoPais, setCodigoPais] = useState(CODIGOS_PAIS[0]);
  const {
    error,
    message,
    loading,
    success,
    CFDI,
    ProveedorCFDIGetService,
    ProveedorAddService,
    ProveedorUpdateService,
  } = useProveedor();
  const {Clientes, ClientesGetService} = useProyectos();

  // Super admin / administrador / contabilidad no tienen un cliente propio
  // (cliente_id les llega en 0): antes el proveedor se guardaba sin quedar
  // enlazado a ningún cliente real y quedaba invisible en cualquier listado,
  // y editar tampoco permitía corregir un enlace roto/equivocado. Tienen que
  // elegir a qué cliente pertenece, tanto al agregar como al editar.
  const necesitaElegirCliente = veTodos;

  useEffect(() => {
    if (necesitaElegirCliente) {
      ClientesGetService(token);
    }
  }, [necesitaElegirCliente, ClientesGetService]);

  useEffect(() => {
    if (!modal) {
      setSelectedCliente(null);
      setSelectedTipo([]);
      setSelectedCFDI([]);
      setCodigoPais(CODIGOS_PAIS[0]);
      return;
    }
    if (Update && clienteActualId) {
      const actual = Clientes.find((c) => String(c.value) === String(clienteActualId));
      setSelectedCliente(actual ?? null);
    }
  }, [modal, Update, clienteActualId, Clientes]);

  // El teléfono se guarda como "+52 2227796648": al editar hay que separar
  // el código del número para mostrarlos en sus propios campos.
  useEffect(() => {
    if (!modal || !Update) return;
    const {codigo, numero} = partirTelefono(Telefono);
    const opcion = CODIGOS_PAIS.find((c) => c.value === codigo) ?? CODIGOS_PAIS[0];
    setCodigoPais(opcion);
    setTelefono(numero);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, Update]);

  // Los Select de abajo sólo muestran su valor real cuando coincide con algo
  // en "options": antes, al editar, TipoPersona/UsoCFDI ya traían el dato del
  // proveedor pero el Select seguía mostrando el placeholder en gris, como si
  // el campo estuviera vacío.
  useEffect(() => {
    if (!modal || !Update) return;
    if (TipoPersona) {
      const actual = Tipo.find((t) => t.value === TipoPersona);
      if (actual) setSelectedTipo(actual);
    }
  }, [modal, Update, TipoPersona]);

  useEffect(() => {
    if (!modal || !Update) return;
    if (UsoCFDI && CFDI.length > 0) {
      const actual = CFDI.find((c) => String(c.value) === String(UsoCFDI));
      if (actual) setSelectedCFDI(actual);
    }
  }, [modal, Update, UsoCFDI, CFDI]);

  useEffect(() => {
    if (success === true) {
      ProveedorGetService(veTodos ? 0 : cliente_id, token);
    }
  }, [ProveedorGetService, success]);

  useEffect(() => {
    ProveedorCFDIGetService(token);
  }, [ProveedorCFDIGetService]);

  const createUser = () => {
    if (necesitaElegirCliente && !SelectedCliente) {
      Swal.fire({
        icon: "error",
        title: "Falta el cliente",
        text: "Selecciona a qué cliente pertenece este proveedor.",
      });
      return;
    }

    if (Telefono && Telefono.length !== 10) {
      Swal.fire({
        icon: "error",
        title: "Teléfono incompleto",
        text: "El teléfono debe tener 10 dígitos.",
      });
      return;
    }

    const telefonoCompleto = Telefono ? `${CodigoPais.value} ${Telefono}` : "";

    if (Update) {
      const data = new FormData();
      data.append("id", Id);
      data.append("tipo_persona", TipoPersona);
      data.append("nombre", Nombre);
      data.append("apellido_p", ApellidoP);
      data.append("apellido_m", ApellidoM);
      data.append("RFC", RFC);
      data.append("correo", Correo);
      data.append("telefono", telefonoCompleto);
      data.append("cfdi", UsoCFDI);
      data.append("banco", Banco);
      data.append("clabe", Clabe);
      data.append("cliente_id", necesitaElegirCliente ? SelectedCliente.value : cliente_id);

      ProveedorUpdateService(data, token);

      Swal.fire({
        title: "Actualizando proveedor",
        text: "Loading...",
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        icon: "question",
        onOpen: () => {
          Swal.showLoading();
        },
      });
    } else {
      const data = new FormData();
      data.append("tipo_persona", TipoPersona);
      data.append("nombre", Nombre);
      data.append("apellido_p", ApellidoP);
      data.append("apellido_m", ApellidoM);
      data.append("RFC", RFC);
      data.append("correo", Correo);
      data.append("telefono", telefonoCompleto);
      data.append("cfdi", UsoCFDI);
      data.append("banco", Banco);
      data.append("clabe", Clabe);
      data.append("cliente_id", necesitaElegirCliente ? SelectedCliente.value : cliente_id);

      ProveedorAddService(data, token);

      Swal.fire({
        title: "Registrando proveedor",
        text: "Loading...",
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        icon: "question",
        onOpen: () => {
          Swal.showLoading();
        },
      });
    }
  };

  useEffect(() => {
    if (error) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Error",
        confirmButtonColor: "#3b82f6",
        text: message,
      });
    }
  }, [message, error]);

  useEffect(() => {
    if (success) {
      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Exito",
        confirmButtonColor: "#3b82f6",
        text: message,
      });
      setTipoPersona("");
      setSelectedTipo("");
      setNombre("");
      setApellidoP("");
      setApellidoM("");
      setRFC("");
      setCorreo("");
      setTelefono("");
      setUsoCFDI("");
      setBanco("");
      setClabe("");

      toggle();
    }
  }, [message, success]);

  return (
    <>
      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader className="backend_titulo" toggle={toggle}>
          {Update ? "Editar proveedor" : "Agregar proveedor"}
        </ModalHeader>
        <ModalBody>
          {necesitaElegirCliente && (
            <div className="crud-modal-field">
              <label className="crud-modal-label">Cliente</label>
              <Select
                value={SelectedCliente}
                onChange={(e) => setSelectedCliente(e)}
                options={Clientes}
                placeholder="¿A qué cliente pertenece?"
              />
            </div>
          )}
          <div className="crud-modal-field">
            <label className="crud-modal-label">Tipo de persona</label>
            <Select
              value={SelectedTipo}
              onChange={(e) => {
                setTipoPersona(e.value);
                setSelectedTipo(e);
              }}
              options={Tipo}
              placeholder={TipoPersona ? TipoPersona : "Tipo de persona"}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">
              {TipoPersona === "Fisica" ? "Nombre" : "Razón social"}
            </label>
            <Input
              placeholder={
                TipoPersona === "Fisica" ? "Nombre" : "Razon Social"
              }
              value={Nombre}
              onChange={(e) => setNombre(aTituloDeCaso(e.target.value))}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">Apellido paterno</label>
            <Input
              placeholder="Apellido Paterno"
              value={ApellidoP}
              onChange={(e) => setApellidoP(aTituloDeCaso(e.target.value))}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">Apellido materno</label>
            <Input
              placeholder="Apellido Materno"
              value={ApellidoM}
              onChange={(e) => setApellidoM(aTituloDeCaso(e.target.value))}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">RFC</label>
            <Input
              placeholder="RFC"
              value={RFC}
              onChange={(e) => setRFC(e.target.value.toUpperCase())}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">Correo electrónico</label>
            <Input
              type="email"
              placeholder="correo@ejemplo.com"
              value={Correo}
              onChange={(e) => setCorreo(e.target.value)}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">Teléfono</label>
            <div className="d-flex gap-2">
              <div style={{minWidth: "11rem"}}>
                <Select
                  value={CodigoPais}
                  onChange={(e) => setCodigoPais(e)}
                  options={CODIGOS_PAIS}
                />
              </div>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="10 dígitos"
                value={Telefono}
                onChange={(e) => setTelefono(e.target.value.replace(/\D/g, "").slice(0, 10))}
              />
            </div>
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">Uso de CFDI</label>
            <Select
              value={SelectedCFDI}
              onChange={(e) => {
                setUsoCFDI(e.value);
                setSelectedCFDI(e);
              }}
              options={CFDI}
              placeholder={UsoCFDI ? UsoCFDI : "CFDI"}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">Banco</label>
            <Input
              placeholder="Ej. BBVA, Santander..."
              value={Banco}
              onChange={(e) => setBanco(e.target.value)}
            />
          </div>
          <div className="crud-modal-field">
            <label className="crud-modal-label">CLABE interbancaria</label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="18 dígitos"
              value={Clabe}
              onChange={(e) => setClabe(e.target.value.replace(/\D/g, "").slice(0, 18))}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="backend_boton" onClick={() => createUser()}>
            {Update ? "Guardar cambios" : "Agregar"}
          </Button>{" "}
        </ModalFooter>
      </Modal>
    </>
  );
}
